/**
 * RowStateTag — DataSet `_state` ('C'/'U'/'D'/'') 을 Tag 로 변환하는 공용 컴포넌트.
 *
 * 【역할】
 * - 편집 가능한 그리드에서 행의 변경 상태(Create/Update/Delete) 를 컬러 Tag 로 시각화.
 * - 반복적인 삼항 분기(`s === 'C' ? <Tag> : ...`) 제거.
 *
 * 【주요 기능】
 * - state === 'C' → green Tag (TTL_ADD '신규')
 * - state === 'U' → orange Tag (TTL_EDIT '수정')
 * - state === 'D' → red Tag (BTN_DELETE '삭제')
 * - 그 외 null (미렌더)
 *
 * 【Props】
 * - state: RowState | undefined — 행 상태 ('C'/'U'/'D'/'' 또는 임의 문자열).
 * - createColor?: string — Create 색상 (기본 green).
 * - updateColor?: string — Update 색상 (기본 orange).
 * - deleteColor?: string — Delete 색상 (기본 red).
 *
 * 【사용 예시】
 * ```tsx
 * <RowStateTag state={row._state} />
 * ```
 *
 * 기존 반복 패턴:
 *   {s === 'C' ? <Tag color="green">{t('TTL_ADD', '신규')}</Tag>
 *    : s === 'U' ? <Tag color="orange">{t('TTL_EDIT', '수정')}</Tag>
 *    : null}
 */
import { Tag } from 'antd'
import { useLabel } from '@/framework/useLabel'

type RowState = 'C' | 'U' | 'D' | '' | string

interface Props {
  /** 행 상태 ('C'/'U'/'D'/'' 또는 임의 문자열). 'C'/'U'/'D' 외에는 렌더하지 않음. */
  state: RowState | undefined
  /** Create 색상 (기본 green). */
  createColor?: string
  /** Update 색상 (기본 orange). */
  updateColor?: string
  /** Delete 색상 (기본 red). */
  deleteColor?: string
}

/**
 * RowStateTag — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function RowStateTag({
  state,
  createColor = 'green',
  updateColor = 'orange',
  deleteColor = 'red'
}: Props) {
  const { t } = useLabel()
  if (state === 'C') return <Tag color={createColor}>{t('TTL_ADD', '신규')}</Tag>
  if (state === 'U') return <Tag color={updateColor}>{t('TTL_EDIT', '수정')}</Tag>
  if (state === 'D') return <Tag color={deleteColor}>{t('BTN_DELETE', '삭제')}</Tag>
  return null
}
